import React, { useEffect, useState, useRef, lazy, memo, createElement, Component } from 'react';
import { CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon, ShieldCheckIcon, ShoppingCartIcon, GiftIcon, BookOpenIcon, TruckIcon, MessageCircleIcon, LockIcon, AwardIcon, ClockIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
// ─── Checkout URL builder ─────────────────────────────────────────────────────
const buildUrl = (template: string) => {
  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get('fbclid');
  const clickId = params.get('click_id') || sessionStorage.getItem('cf_click_id') || localStorage.getItem('cf_click_id');
  let url = template;
  if (fbclid) {
    url = url.replace('{{fbclid}}', fbclid);
  } else {
    url = url.replace('&fbclid={{fbclid}}', '').replace('fbclid={{fbclid}}', '');
  }
  if (clickId) {
    const separator = url.includes('?') ? '&' : '?';
    // If it's a shopify cart link, ensure 'checkout' is handled correctly
    if (url.includes('/cart/')) {
      const hasCheckout = url.includes('checkout');
      const cleanUrl = url.replace(/[?&]checkout/, '');
      url = cleanUrl + (cleanUrl.includes('?') ? '&' : '?') + 'click_id=' + encodeURIComponent(clickId) + (hasCheckout ? '&checkout' : '');
    } else {
      url = url + separator + 'click_id=' + encodeURIComponent(clickId);
    }
  }
  return url;
};
// ─── Tracking ─────────────────────────────────────────────────────────────────
declare global {
  interface Window {
    fbq: (action: string, event: string, params?: any) => void;
  }
}
const trackV5 = (_name: string, _price: number) => {
  // AddToCart/InitiateCheckout removed
};
// ─── Checkout URLs & Pricing ──────────────────────────────────────────────────
const CHECKOUT_DIGITAL = 'https://legendaryreads.com/cart/42231842701394:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_PREMIUM = 'https://legendaryreads.com/cart/42285346947154:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_BUNDLE = 'https://legendaryreads.com/cart/42285352288338:1?checkout&fbclid={{fbclid}}';
type TierKey = 'DIGITAL' | 'PREMIUM' | 'BUNDLE';
interface TierInfo {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  url: string;
  desc: string;
  badge?: string;
}
const TIERS: Record<TierKey, TierInfo> = {
  DIGITAL: {
    id: 'digital',
    name: 'Complete Digital Collection',
    price: 59.97,
    originalPrice: 114.97,
    url: CHECKOUT_DIGITAL,
    desc: '4 Digital Books (Instant Access)'
  },
  PREMIUM: {
    id: 'premium',
    name: 'Premium Hardcover Bundle',
    price: 94.97,
    originalPrice: 127.97,
    url: CHECKOUT_PREMIUM,
    desc: 'Digital Collection + Premium Hardcover',
    badge: 'MOST POPULAR'
  },
  BUNDLE: {
    id: 'bundle',
    name: "Time Traveler's Collector Bundle",
    price: 279.97,
    originalPrice: 419.97,
    url: CHECKOUT_BUNDLE,
    desc: 'Complete Digital Collection + Premium Hardcover + 3 Companion Books (Paperback) + Audiobook + Trucker Hat + Framed Print + Tote Bag + Commemorative Mug'
  }
};
const TIER_KEYS: TierKey[] = ['DIGITAL', 'PREMIUM', 'BUNDLE'];
// ─── Images ───────────────────────────────────────────────────────────────────
const GALLERY_IMAGES = ['https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png', 'https://cdn.magicpatterns.com/uploads/2mCBcB1vUDxS6qefycewPD/TIME_TRAVELLERS_COLLECTOR_BUNDLED.png', 'https://cdn.magicpatterns.com/uploads/7BQJLvNZKyzy2Jn4rjWLvk/bit_audiobook.png', 'https://cdn.magicpatterns.com/uploads/8av42GqLUN9WRwXAhxHRBi/Generated_Image_November_18,_2025_-_3_14PM.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/i3DKchuX8RMmzYrqAhpK13/foam-trucker-hat-black-one-size-right-front-6914c3a4781f3.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/rbwPKtwrCHTaFNqFeaH2fA/enhanced-matte-paper-framed-poster-%28in%29-white-10x10-front-6914bc8d8b378.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2eQBo88LwYLe7TLxEHMDQe/white-glossy-mug-white-11-oz-handle-on-left-6914d9d1888e4.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/dsPxuWW7a4Xja3nWKMPU8M/all-over-print-tote-bag-yellow-15x15-mockup-69123afa061d2.jpg'];
const LOOK_INSIDE_IMAGES = [
// Book 1: Back In Time
'https://cdn.magicpatterns.com/uploads/tq45Thpd45LDyHLHFRyHb4/image.png', 'https://cdn.magicpatterns.com/uploads/xAZSGuTLPmciaBSXGgJTHj/image.png', 'https://cdn.magicpatterns.com/uploads/g3EfcxHfyFE6HpXeHQ2aZA/image.png',
// Book 2: Behind The Clock Tower
'https://cdn.magicpatterns.com/uploads/rerwYoQ8gs22KNkXTHs5Gf/image.png', 'https://cdn.magicpatterns.com/uploads/rLQyuhnh2qhC64hG3pEah5/image.png', 'https://cdn.magicpatterns.com/uploads/vxi7ma652Y1wW88rpSxwZe/image.png',
// Book 3: Great Scott! 40 Reflections
'https://cdn.magicpatterns.com/uploads/gzWz855tfQDUeagTytQYCe/image.png', 'https://cdn.magicpatterns.com/uploads/3NyPkbzUAwACdE2zMZ8MuJ/image.png', 'https://cdn.magicpatterns.com/uploads/aN6H3njHZtV8jQu2BhuyAk/image.png',
// Book 4: Time. Imagination. Destiny.
'https://cdn.magicpatterns.com/uploads/vRUrFezUsXr4NeCCTTcL6b/image.png', 'https://cdn.magicpatterns.com/uploads/7sMQijfdPvvuxFFNxak5uH/image.png', 'https://cdn.magicpatterns.com/uploads/4LoVuiUpCZ3tw4hi51F4TM/image.png'];
const MINI_REVIEWS = [{
  quote: 'A masterpiece for collectors. If you grew up with Marty and Doc, this is your time machine.',
  name: 'Newman Family',
  loc: 'Boston'
}, {
  quote: 'This book took me straight back to the smell of popcorn and the sound of Huey Lewis.',
  name: 'Kevin R.',
  loc: 'San Diego'
}, {
  quote: 'Reading this felt like watching the trilogy for the first time again.',
  name: 'Laura P.',
  loc: 'Chicago'
}, {
  quote: 'The behind-the-scenes stories made me appreciate just how much heart went into this film.',
  name: 'Brandon T.',
  loc: 'Orlando'
}, {
  quote: "I've collected BTTF merchandise for 20 years. This is the first time I've read something that actually understood why it matters.",
  name: 'Mark T.',
  loc: 'Columbus'
}];
const FULL_REVIEWS = [{
  quote: "I've collected BTTF merchandise for 20 years. Steelbooks, prints, the whole thing. This is the first time I've read something about this film that made me feel like the person who made it actually understood why it matters. Read the whole flagship book in one night.",
  name: 'Mark T., 47',
  loc: 'Columbus'
}, {
  quote: "My son is 13. He'd seen the trilogy twice but didn't really get why I treat it like it's sacred. I read him the section on why fathers show this film to their kids. He went quiet for a second and then said 'that's literally us right now, Dad.'",
  name: 'Sandra R., 52',
  loc: 'Austin'
}, {
  quote: "I went in skeptical. Behind The Clock Tower alone destroyed that pattern. The DeLorean hairdryer story, the Stoltz reshoot, the 18-hour Fox schedule — I've been a fan for 35 years and I didn't know any of it.",
  name: 'David M., 39',
  loc: 'San Diego'
}, {
  quote: "This book took me straight back to the smell of popcorn and the sound of Huey Lewis. It's pure nostalgia.",
  name: 'Kevin R.',
  loc: 'San Diego'
}, {
  quote: 'Reading this felt like watching the trilogy for the first time again. I had tears in my eyes by Chapter Two.',
  name: 'Laura P.',
  loc: 'Chicago'
}, {
  quote: 'The behind-the-scenes stories made me appreciate just how much heart went into this film.',
  name: 'Brandon T.',
  loc: 'Orlando'
}];
const FAQ_ITEMS = [{
  q: 'What formats are the books available in?',
  a: 'The books are available in digital (PDF, instant download) and physical (premium hardcover or paperback, printed on demand). Some bundles include both formats. Digital editions are compatible with all devices — phones, tablets, computers, and e-readers.'
}, {
  q: 'How quickly will I receive my order?',
  a: "Digital editions are delivered instantly on the thank-you page after checkout and also sent to your email.\n\nPhysical books are professionally printed on demand and typically arrive within 9–14 days. The Time Traveler's Collector Bundle ships in 8–10 business days. Once your order ships, you'll receive a tracking email automatically."
}, {
  q: 'Is this official/licensed content?',
  a: 'No. This is an independent fan publication created to celebrate film history and cultural impact. It is not affiliated with, endorsed by, or licensed by Universal City Studios LLC or Amblin Entertainment. "Back to the Future™" is a trademark of Universal City Studios LLC.\n\nAll original written material, design, commentary, and analysis are protected under copyright as original works of authorship owned by Legendary Reads.'
}, {
  q: 'What makes this different from other BTTF books?',
  a: "Because it wasn't made to be fast or disposable.\n\nThis collection took two years of deep research, careful writing, multiple revisions, and a clear editorial vision. It is not recycled Wikipedia content, not AI-generated filler, and not a coffee table book with photos you've seen a thousand times.\n\nIt's the comprehensive deep-dive true fans have been waiting for."
}, {
  q: 'Why only 1,985 copies?',
  a: "1985 is the year Back to the Future was released. The physical edition is limited to exactly 1,985 copies as a tribute to that moment — making each copy a genuine collector's item. Once they're gone, they won't be reprinted."
}, {
  q: 'Can I gift this to someone?',
  a: "Yes. Simply enter the recipient's shipping address at checkout for physical editions. For digital editions, the download link is sent to the email used at checkout — you can forward it to the recipient or purchase using their email address."
}, {
  q: 'Are there future updates or additions?',
  a: 'Digital edition buyers may receive updates if new content is added to the collection. Physical editions are fixed at the time of printing. We recommend joining our email list to stay informed about any future releases or companion volumes.'
}, {
  q: 'Who wrote this book?',
  a: 'This collection was written by The VHS Club, a dedicated team of writers and researchers who spent years researching, organizing, and refining the content.\n\nThis was not rushed, outsourced, or assembled from recycled material — it was created intentionally for true fans.'
}, {
  q: 'Is this book generated entirely by AI?',
  a: 'No.\n\nAll research, writing, analysis, and structure were created by humans over an extended period of time. AI tools were only used in limited ways, such as formatting, organization, and final layout assistance.\n\nAI was used as a tool, not as the author.'
}, {
  q: 'Is there an audiobook version?',
  a: "Yes. The Back In Time Audiobook Experience is included exclusively in the Time Traveler's Collector Bundle. It's a professionally produced audio version of the main book — perfect for listening on the go."
}, {
  q: 'What if I have an issue with my order?',
  a: "Please contact us directly at info.legendaryreads@gmail.com and we'll be happy to help.\n\nFor the fastest resolution, we always recommend reaching out to us before contacting your bank or card provider."
}];
// Shop Pay installment amounts per tier
const SHOP_PAY_AMOUNTS: Record<TierKey, string> = {
  DIGITAL: '$15',
  PREMIUM: '$23.75',
  BUNDLE: '$70'
};
// ═══ SECTION 1: ANNOUNCEMENT BAR ═══
const AnnouncementBar = () => <div className="bg-[#0f172a] text-white text-center py-2 px-4 text-xs md:text-sm font-medium tracking-wide" data-id="element-2064">
    40th Anniversary Limited Offer — Main Book + 3 Companion Books FREE
  </div>;
// ═══ SECTION 2: HEADER ═══
const HeaderV5 = () => <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm" data-id="element-2065">
    <div className="container mx-auto px-4 py-3 flex justify-between items-center max-w-7xl" data-id="element-2066">
      <div className="font-bold text-xl md:text-2xl tracking-tight text-[#1a1a2e]" style={{
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-2067">
        Back In Time
      </div>
      <a href="#purchase-panel" className="cta font-semibold text-sm px-4 py-2 rounded transition-colors" style={{
      backgroundColor: '#16a34a',
      color: 'white',
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-2068">
        Get My Copy
      </a>
    </div>
  </header>;
// ═══ SECTION 3: HERO PRODUCT SECTION ═══
const HeroProductSection = () => {
  const [mainImage, setMainImage] = useState(GALLERY_IMAGES[0]);
  const [selectedTier, setSelectedTier] = useState<TierKey>('DIGITAL');
  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedRef = useRef(false);
  const {
    formatted,
    secondsLeft
  } = useCountdown();
  const currentTier = TIERS[selectedTier];
  const checkoutUrl = buildUrl(currentTier.url);
  const savings = currentTier.originalPrice - currentTier.price;
  const savingsPercent = Math.round(savings / currentTier.originalPrice * 100);
  const hours = String(Math.floor(secondsLeft / 3600)).padStart(2, '0');
  const minutes = String(Math.floor(secondsLeft % 3600 / 60)).padStart(2, '0');
  const secs = String(secondsLeft % 60).padStart(2, '0');
  const handleCta = () => {
    trackV5(currentTier.name, currentTier.price);
  };
  // IntersectionObserver for ClickMagick tracking
  useEffect(() => {
    if (!sectionRef.current || hasTrackedRef.current) return;
    const isMobile = window.innerWidth < 768;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasTrackedRef.current && !sessionStorage.getItem('pricing_section_viewed')) {
          hasTrackedRef.current = true;
          sessionStorage.setItem('pricing_section_viewed', 'true');
          const delay = isMobile ? 1000 : 500;
          setTimeout(() => {
            const ctaTrigger = document.createElement('div');
            ctaTrigger.className = 'cta';
            ctaTrigger.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;top:0;left:0;';
            ctaTrigger.setAttribute('data-pricing-trigger', 'true');
            document.body.appendChild(ctaTrigger);
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            ctaTrigger.dispatchEvent(clickEvent);
            if (isMobile) {
              const touchStartEvent = new TouchEvent('touchstart', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              const touchEndEvent = new TouchEvent('touchend', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              ctaTrigger.dispatchEvent(touchStartEvent);
              ctaTrigger.dispatchEvent(touchEndEvent);
            }
            setTimeout(() => {
              if (ctaTrigger.parentNode) ctaTrigger.parentNode.removeChild(ctaTrigger);
            }, 2000);
          }, delay);
        }
      });
    }, {
      threshold: isMobile ? 0.1 : 0.3,
      rootMargin: '0px'
    });
    observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  return <section className="py-4 md:py-8 bg-white" ref={sectionRef} id="purchase-panel" data-id="element-2069">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-2070">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-10" data-id="element-2071">
          {/* LEFT: Image Gallery */}
          <div className="w-full lg:w-1/2" data-id="element-2072">
            <div className="mb-2 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 aspect-square flex items-center justify-center" data-id="element-2073">
              <img src={mainImage} alt="Product View" className="w-full h-full object-contain p-4" fetchpriority="high" width="600" height="600" data-id="element-2074" />
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-1" data-id="element-2075">
              {GALLERY_IMAGES.map((img, idx) => <button key={idx} onClick={() => setMainImage(img)} className={`flex-shrink-0 w-14 h-14 md:w-18 md:h-18 border-2 rounded overflow-hidden transition-all ${mainImage === img ? 'border-[#1a1a2e]' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-2076">
                  <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" decoding="async" width="72" height="72" data-id="element-2077" />
                </button>)}
            </div>
            <a href="#look-inside" className="inline-block mt-1 text-sm text-gray-600 underline underline-offset-2 hover:text-[#1a1a2e]" data-id="element-2078">
              Look inside the books
            </a>
          </div>

          {/* RIGHT: Product Info */}
          <div className="w-full lg:w-1/2 flex flex-col" data-id="element-2079">
            {/* Reviews */}
            <div className="flex items-center gap-2 mb-1.5" data-id="element-2080">
              <div className="flex gap-0.5" data-id="element-2081">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-amber-500 fill-amber-500" data-id="element-2082" />)}
              </div>
              <a href="#customers-love-us" className="text-sm text-gray-600 font-medium underline decoration-gray-300 underline-offset-2 hover:text-[#1a1a2e]" data-id="element-2083">
                5,354+ verified reviews
              </a>
            </div>

            {/* Title & Subtitle */}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1a1a2e] mb-1.5 leading-tight" style={{
            fontFamily: "'Playfair Display', Georgia, serif"
          }} data-id="element-2084">
              Back In Time: 40th Anniversary Collector's Edition
            </h1>
            <p className="text-gray-600 text-sm md:text-base mb-3 leading-relaxed" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-2085">
              The most complete legacy document ever assembled for Back to the
              Future. Includes final 40th anniversary content (2025).
            </p>

            {/* Trust Badges — single column on mobile for space optimization */}
            <div className="flex flex-col md:grid md:grid-cols-2 gap-1.5 md:gap-y-2 md:gap-x-3 mb-3 text-sm text-gray-700" data-id="element-2086">
              <div className="flex items-center gap-1.5" data-id="element-2087">
                <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" data-id="element-2088" />{' '}
                Digital + Print Available
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2089">
                <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" data-id="element-2090" />{' '}
                Direct from publisher
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2091">
                <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" data-id="element-2092" />{' '}
                Instant digital delivery
              </div>
            </div>

            {/* Sale Badge & Price */}
            <div className="mb-3" data-id="element-2093">
              <span className="inline-block bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide mb-1.5" data-id="element-2094">
                40th Anniversary Sale
              </span>
              <div className="flex items-baseline gap-2" data-id="element-2095">
                <span className="text-red-600 font-bold text-xl md:text-2xl" data-id="element-2096">
                  {savingsPercent}% OFF
                </span>
                <span className="text-[#1a1a2e] font-bold text-xl md:text-2xl" data-id="element-2097">
                  ${currentTier.price.toFixed(2)}
                </span>
              </div>
              <div className="text-gray-500 text-sm" data-id="element-2098">
                Regular price:{' '}
                <span className="line-through" data-id="element-2099">
                  ${currentTier.originalPrice.toFixed(2)}
                </span>
              </div>
              <div className="mt-1.5" data-id="element-2100">
                <span className="inline-flex items-center gap-1 bg-green-100 border border-green-300 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full" data-id="element-2101">
                  You Save ${savings.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Order Bumps */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-3" data-id="element-2102">
              <h3 className="font-semibold text-[#1a1a2e] mb-2 text-sm" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-2103">
                Select your edition:
              </h3>
              <div className="space-y-2" data-id="element-2104">
                {TIER_KEYS.map(key => {
                const tier = TIERS[key];
                const isSelected = selectedTier === key;
                const shopPayAmount = SHOP_PAY_AMOUNTS[key];
                return <label key={key} className={`relative flex items-start p-2.5 border rounded-lg cursor-pointer transition-all ${isSelected ? 'border-green-600 bg-green-50/40 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-2105">
                      {tier.badge && <span className="absolute -top-2 right-3 bg-amber-100 text-amber-800 text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-amber-200" data-id="element-2106">
                          {tier.badge}
                        </span>}
                      <div className="flex items-center h-5 mt-0.5" data-id="element-2107">
                        <input type="radio" name="tier" className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500" checked={isSelected} onChange={() => setSelectedTier(key)} data-id="element-2108" />
                      </div>
                      <div className="ml-3 flex-1" data-id="element-2109">
                        <div className="flex justify-between items-start" data-id="element-2110">
                          <span className="font-semibold text-[#1a1a2e] text-sm" data-id="element-2111">
                            {tier.name}
                          </span>
                          <span className="font-bold text-[#1a1a2e] text-sm ml-2" data-id="element-2112">
                            ${tier.price.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-gray-500 text-xs mt-0.5" data-id="element-2113">
                          {tier.desc}
                        </p>
                        <div className="mt-0.5" data-id="element-2114">
                          <span className="text-green-600 text-[10px] font-bold" data-id="element-2115">
                            You Save $
                            {(tier.originalPrice - tier.price).toFixed(2)}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center gap-1 text-[11px] text-gray-500" data-id="element-2116">
                          <span data-id="element-2117">or 4 payments of</span>
                          <span className="font-bold text-[#5a31f4]" data-id="element-2118">
                            {shopPayAmount}
                          </span>
                          <span data-id="element-2119">with</span>
                          <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-3.5 inline-block" width="48" height="14" loading="lazy" decoding="async" data-id="element-2120" />
                        </div>
                        <p className="text-[10px] text-gray-400" data-id="element-2121">
                          Biweekly interest-free payments
                        </p>
                      </div>
                    </label>;
              })}
              </div>
            </div>

            {/* CTA */}
            <a href={checkoutUrl} onClick={handleCta} className="cta flex items-center justify-center gap-2 w-full text-center font-bold py-3 rounded-lg transition-all text-white text-base" style={{
            backgroundColor: '#16a34a',
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-2122">
              Get My Collector's Edition
            </a>

            {/* Below CTA: Countdown Timer */}
            <div className="flex items-center justify-center gap-3 mt-2" data-id="element-2123">
              <div className="flex items-center gap-1" data-id="element-2124">
                <div className="bg-gray-800 text-white text-xs font-bold px-1.5 py-1 rounded min-w-[28px] text-center" data-id="element-2125">
                  {hours}
                </div>
                <span className="text-[9px] text-gray-500 uppercase font-semibold" data-id="element-2126">
                  hrs
                </span>
                <div className="bg-gray-800 text-white text-xs font-bold px-1.5 py-1 rounded min-w-[28px] text-center" data-id="element-2127">
                  {minutes}
                </div>
                <span className="text-[9px] text-gray-500 uppercase font-semibold" data-id="element-2128">
                  min
                </span>
                <div className="bg-gray-800 text-white text-xs font-bold px-1.5 py-1 rounded min-w-[28px] text-center" data-id="element-2129">
                  {secs}
                </div>
                <span className="text-[9px] text-gray-500 uppercase font-semibold" data-id="element-2130">
                  sec
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-gray-500" data-id="element-2131">
                <ClockIcon className="h-4 w-4" data-id="element-2132" /> lock in savings now
              </div>
            </div>

            {/* Secure Checkout + Payment Icons */}
            <div className="flex items-center justify-center gap-2.5 mt-3" data-id="element-2133">
              <div className="flex items-center gap-1" data-id="element-2134">
                <LockIcon className="h-3.5 w-3.5 text-gray-600" data-id="element-2135" />
                <span className="text-[10px] font-bold uppercase text-gray-600 tracking-wide" data-id="element-2136">
                  Secure Checkout
                </span>
              </div>
              <div className="flex gap-1 items-center" data-id="element-2137">
                <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-5" width="60" height="20" loading="lazy" decoding="async" data-id="element-2138" />
                <svg className="h-5 w-8" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-2139">
                  <rect width="38" height="24" rx="4" fill="#000" data-id="element-2140" />
                  <text x="19" y="15" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold" fontFamily="system-ui" data-id="element-2141">
                    Pay
                  </text>
                </svg>
                <svg className="h-5 w-8" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-2142">
                  <rect width="38" height="24" rx="4" fill="#fff" stroke="#ddd" data-id="element-2143" />
                  <text x="19" y="15" textAnchor="middle" fill="#5F6368" fontSize="8" fontWeight="bold" fontFamily="system-ui" data-id="element-2144">
                    G Pay
                  </text>
                </svg>
                <svg className="h-5 w-8" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-2145">
                  <rect width="38" height="24" rx="4" fill="#1A1F71" data-id="element-2146" />
                  <text x="19" y="16" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold" fontStyle="italic" fontFamily="system-ui" data-id="element-2147">
                    VISA
                  </text>
                </svg>
                <svg className="h-5 w-8" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-2148">
                  <rect width="38" height="24" rx="4" fill="#fff" stroke="#ddd" data-id="element-2149" />
                  <circle cx="15" cy="12" r="7" fill="#EB001B" data-id="element-2150" />
                  <circle cx="23" cy="12" r="7" fill="#F79E1B" data-id="element-2151" />
                  <path d="M19 7.3a7 7 0 010 9.4 7 7 0 000-9.4z" fill="#FF5F00" data-id="element-2152" />
                </svg>
                <svg className="h-5 w-8" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-2153">
                  <rect width="38" height="24" rx="4" fill="#006FCF" data-id="element-2154" />
                  <text x="19" y="16" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="bold" fontFamily="system-ui" data-id="element-2155">
                    AMEX
                  </text>
                </svg>
              </div>
            </div>

            {/* Bundle and save */}
            <button onClick={() => setSelectedTier('BUNDLE')} className="mt-3 w-full py-2 rounded-lg border border-gray-300 bg-[#1a1a2e] text-white text-sm font-semibold hover:bg-[#2a2a3e] transition-colors" data-id="element-2156">
              ⊕ Bundle and save
            </button>
          </div>
        </div>
      </div>
    </section>;
};
// ═══ SECTION 4: SOCIAL PROOF STRIP ═══
const SocialProofStrip = () => {
  const doubledReviews = [...MINI_REVIEWS, ...MINI_REVIEWS];
  return <section className="py-5 md:py-8 bg-gray-50 overflow-hidden" data-id="element-2157">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-2158">
        <h2 className="text-center text-lg md:text-xl font-semibold text-[#1a1a2e] mb-4" style={{
        fontFamily: "'Playfair Display', Georgia, serif"
      }} data-id="element-2159">
          Thousands of happy collectors worldwide
        </h2>
      </div>
      <div className="relative" data-id="element-2160">
        <div className="flex gap-4" style={{
        animation: 'marquee 30s linear infinite',
        width: 'max-content'
      }} data-id="element-2161">
          {doubledReviews.map((r, i) => <div key={i} className="flex-shrink-0 w-64 bg-white border border-gray-200 rounded-lg p-3 shadow-sm" data-id="element-2162">
              <div className="flex gap-0.5 mb-1.5" data-id="element-2163">
                {[...Array(5)].map((_, j) => <StarIcon key={j} className="h-3 w-3 text-amber-500 fill-amber-500" data-id="element-2164" />)}
              </div>
              <p className="text-gray-700 text-xs italic leading-relaxed mb-1.5" data-id="element-2165">
                "{r.quote}"
              </p>
              <p className="text-gray-500 text-[10px] font-medium" data-id="element-2166">
                — {r.name}, {r.loc}
              </p>
            </div>)}
        </div>
      </div>
      <style data-id="element-2167">{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
};
// ═══ SECTION 5: SPECIFICATIONS + ABOUT THE BOOK ═══
const SpecsAndAbout = () => <section className="py-6 md:py-10 bg-white" data-id="element-2168">
    <div className="container mx-auto px-4 max-w-6xl" data-id="element-2169">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12" data-id="element-2170">
        {/* LEFT: Specifications */}
        <div data-id="element-2171">
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2" data-id="element-2172">
            Specifications
          </h3>
          <ul className="space-y-1.5 text-sm text-gray-700" data-id="element-2173">
            <li className="flex items-start gap-2" data-id="element-2174">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2175" />
              <span data-id="element-2176">
                <strong data-id="element-2177">Title:</strong> Back In Time: 40th Anniversary
                Collector's Edition (1985-2025)
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-2178">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2179" />
              <span data-id="element-2180">
                <strong data-id="element-2181">Volumes:</strong> 4 books included
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-2182">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2183" />
              <span data-id="element-2184">
                <strong data-id="element-2185">Format:</strong> Digital (PDF) + Physical editions
                available
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-2186">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2187" />
              <span data-id="element-2188">
                <strong data-id="element-2189">Pages:</strong> 435+ pages total
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-2190">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2191" />
              <span data-id="element-2192">
                <strong data-id="element-2193">Language:</strong> English
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-2194">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2195" />
              <span data-id="element-2196">
                <strong data-id="element-2197">Publisher:</strong> Legendary Reads (The VHS Club)
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-2198">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2199" />
              <span data-id="element-2200">
                <strong data-id="element-2201">Release:</strong> 2025
              </span>
            </li>
          </ul>
        </div>

        {/* RIGHT: About the Book */}
        <div data-id="element-2202">
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2" data-id="element-2203">
            About the Book
          </h3>
          <h4 className="text-xl md:text-2xl font-bold text-[#1a1a2e] mb-3 leading-tight" style={{
          fontFamily: "'Playfair Display', Georgia, serif"
        }} data-id="element-2204">
            Own this keepsake treasury of 40 years of Back to the Future legacy
          </h4>
          <div className="text-sm text-gray-700 leading-relaxed space-y-2" data-id="element-2205">
            <p data-id="element-2206">
              Since 1985, Back to the Future has been more than a movie — it's a
              cultural landmark that shaped how an entire generation understands
              ambition, risk, and the future itself.
            </p>
            <p data-id="element-2207">
              This collection documents the legacy the way it deserves. Not as
              merchandise. Not as a coffee table book with photos you've seen a
              thousand times. As a cultural archive, assembled the way a
              historian approaches a defining moment: from the inside out.
            </p>
            <p data-id="element-2208">
              <strong data-id="element-2209">Inside you'll discover:</strong> Why Eric Stoltz was fired
              after 6 weeks of filming. The 3 AM breakdown that almost killed
              the production. How a last-minute script change saved the entire
              film. The exact moment Zemeckis knew they'd made history.
            </p>
            <p data-id="element-2210">
              190+ pages of cultural analysis. 87 pages of production secrets.
              87 pages of reflections across 40 years. 71 pages on the
              philosophical legacy of identity, free will, and destiny.
            </p>
            <p className="font-semibold" data-id="element-2211">
              Every page earns its place. Zero fluff. Pure insight.
            </p>
            <p className="text-[#FF6C00] font-semibold" data-id="element-2212">
              Don't miss out on this piece of film history — order today.
            </p>
          </div>
          <a href="#purchase-panel" className="inline-block mt-3 text-sm text-green-600 font-medium underline underline-offset-2 hover:text-green-700" data-id="element-2213">
            Want even more? Check out our collection.
          </a>
        </div>
      </div>
    </div>
  </section>;
// ═══ SECTION 6: GIVING AS A GIFT? ═══
const GiftSection = () => <section className="py-6 md:py-10 bg-gray-50" data-id="element-2214">
    <div className="container mx-auto px-4 max-w-2xl text-center" data-id="element-2215">
      <div className="text-3xl mb-2" data-id="element-2216">🎁</div>
      <h2 className="text-xl md:text-2xl font-bold text-[#1a1a2e] mb-2" style={{
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-2217">
        Giving as a gift?
      </h2>
      <p className="text-sm text-gray-600 leading-relaxed mb-3" data-id="element-2218">
        This collection makes the perfect gift for any Back to the Future fan.
        For physical editions, simply enter the recipient's shipping address at
        checkout. For digital editions, the download link is sent to the email
        used at checkout — you can forward it or purchase using their email.
        Print download and place this certificate in present to your gift
        recipient, it explains their gift is on the way.
      </p>
      <a href="#purchase-panel" className="inline-block text-sm text-green-600 font-semibold underline underline-offset-2 hover:text-green-700" data-id="element-2219">
        Shop gift options →
      </a>
    </div>
  </section>;
// ═══ SECTION 7: LOOK INSIDE ═══
const LookInsideSection = () => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const prev = () => setCurrentIdx(i => i === 0 ? LOOK_INSIDE_IMAGES.length - 1 : i - 1);
  const next = () => setCurrentIdx(i => i === LOOK_INSIDE_IMAGES.length - 1 ? 0 : i + 1);
  return <section id="look-inside" className="py-8 md:py-12 bg-gray-50" data-id="element-2220">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-2221">
        <h2 className="text-center text-2xl md:text-3xl font-bold text-[#1a1a2e] mb-2" style={{
        fontFamily: "'Playfair Display', Georgia, serif"
      }} data-id="element-2222">
          Look Inside
        </h2>
        <p className="text-center text-sm md:text-base text-gray-500 mb-8 max-w-xl mx-auto" data-id="element-2223">
          Preview select pages from inside the books. Note: many pages are
          omitted.
        </p>
        <div className="relative max-w-[420px] mx-auto mt-4 mb-8" data-id="element-2224">
          {/* Book Page Container */}
          <div className="relative bg-white rounded-sm shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)] border border-gray-200 overflow-hidden" style={{
          aspectRatio: '3/4'
        }} data-id="element-2225">
            {/* Subtle page fold effect (left edge) */}
            <div className="absolute inset-y-0 left-0 w-8 md:w-12 bg-gradient-to-r from-black/[0.06] to-transparent z-10 pointer-events-none" data-id="element-2226"></div>

            <img src={LOOK_INSIDE_IMAGES[currentIdx]} alt={`Page ${currentIdx + 1}`} className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-2227" />
          </div>

          {/* Navigation Controls */}
          <button onClick={prev} className="absolute -left-4 md:-left-14 top-1/2 -translate-y-1/2 bg-white text-gray-800 p-2.5 md:p-3 rounded-full shadow-lg border border-gray-100 hover:scale-105 hover:text-[#1a1a2e] transition-all z-20 focus:outline-none focus:ring-2 focus:ring-gray-200" aria-label="Previous page" data-id="element-2228">
            <ChevronLeftIcon className="h-5 w-5 md:h-6 md:w-6" data-id="element-2229" />
          </button>
          <button onClick={next} className="absolute -right-4 md:-right-14 top-1/2 -translate-y-1/2 bg-white text-gray-800 p-2.5 md:p-3 rounded-full shadow-lg border border-gray-100 hover:scale-105 hover:text-[#1a1a2e] transition-all z-20 focus:outline-none focus:ring-2 focus:ring-gray-200" aria-label="Next page" data-id="element-2230">
            <ChevronRightIcon className="h-5 w-5 md:h-6 md:w-6" data-id="element-2231" />
          </button>

          {/* Page Counter */}
          <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-white text-gray-600 text-xs font-semibold px-4 py-1.5 rounded-full shadow-sm border border-gray-200 z-20" data-id="element-2232">
            {currentIdx + 1} / {LOOK_INSIDE_IMAGES.length}
          </div>
        </div>
      </div>
    </section>;
};
// ═══ SECTION 8: THREE-COLUMN INFO ═══
const ThreeColumnInfo = () => <section className="py-6 md:py-10 bg-white" data-id="element-2233">
    <div className="container mx-auto px-4 max-w-6xl" data-id="element-2234">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2235">
        {/* Column 1: FAQ */}
        <div data-id="element-2236">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#1a1a2e] mb-3" data-id="element-2237">
            Frequently Asked Questions
          </h3>
          <p className="text-xs text-gray-600 mb-3" data-id="element-2238">
            See FAQs below, or feel free to contact us at
            info.legendaryreads@gmail.com. We're always happy to help!
          </p>
          <div className="space-y-2 text-xs" data-id="element-2239">
            <div data-id="element-2240">
              <strong className="text-[#1a1a2e]" data-id="element-2241">How much is shipping?</strong>
              <p className="text-gray-600 mt-0.5" data-id="element-2242">
                Free shipping on all physical orders.
              </p>
            </div>
          </div>
        </div>

        {/* Column 2: Premium Quality */}
        <div data-id="element-2243">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#1a1a2e] mb-3" data-id="element-2244">
            Premium Quality
          </h3>
          <p className="text-xs text-gray-600 mb-3" data-id="element-2245">
            Our presses output the highest quality books to ensure each book
            will be treasured by readers for a lifetime.
          </p>
          <ul className="space-y-1.5 text-xs text-gray-700" data-id="element-2246">
            <li className="flex items-start gap-2" data-id="element-2247">
              <CheckIcon className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" data-id="element-2248" />
              Exclusively USA printing
            </li>
            <li className="flex items-start gap-2" data-id="element-2249">
              <CheckIcon className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" data-id="element-2250" />
              Fine art ink printing process for vivid tones
            </li>
            <li className="flex items-start gap-2" data-id="element-2251">
              <CheckIcon className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" data-id="element-2252" />
              Superior binding
            </li>
            <li className="flex items-start gap-2" data-id="element-2253">
              <CheckIcon className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" data-id="element-2254" />
              For hardcover books: a 120 pt. case board
            </li>
            <li className="flex items-start gap-2" data-id="element-2255">
              <CheckIcon className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" data-id="element-2256" />
              Heavy art paper meant to last
            </li>
          </ul>
        </div>

        {/* Column 3: Unique Gift Idea */}
        <div data-id="element-2257">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#1a1a2e] mb-3" data-id="element-2258">
            Unique Gift Idea
          </h3>
          <p className="text-xs text-gray-600 mb-3" data-id="element-2259">
            Did you know — many of our customers purchase as gifts?
          </p>
          <p className="text-xs text-gray-700 leading-relaxed" data-id="element-2260">
            You can buy another copy to send as gifts to loved ones.
          </p>
        </div>
      </div>
    </div>
  </section>;
// ═══ SECTION 9: WHO AM I BUYING FROM? ═══
const WhoAmIBuyingFrom = () => <section className="py-6 md:py-10 bg-[#0f172a] text-white" data-id="element-2261">
    <div className="container mx-auto px-4 max-w-5xl text-center" data-id="element-2262">
      <h2 className="text-xl md:text-2xl font-bold mb-3" style={{
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-2263">
        Who am I buying from?
      </h2>
      <p className="text-sm text-gray-300 mb-6 max-w-2xl mx-auto leading-relaxed" data-id="element-2264">
        We are Legendary Reads, an independent publisher of premium books and
        collectible products. We've been serving fans for years, which is why
        our customers give us such great reviews. Here are a few ways we provide
        value:
      </p>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-5" data-id="element-2265">
        {[{
        icon: <BookOpenIcon className="h-5 w-5" data-id="element-2266" />,
        label: 'Unique high-quality products'
      }, {
        icon: <StarIcon className="h-5 w-5" data-id="element-2267" />,
        label: '5,354+ verified reviews'
      }, {
        icon: <TruckIcon className="h-5 w-5" data-id="element-2268" />,
        label: 'Fast shipping'
      }, {
        icon: <CheckIcon className="h-5 w-5" data-id="element-2269" />,
        label: 'Satisfaction guarantee'
      }, {
        icon: <MessageCircleIcon className="h-5 w-5" data-id="element-2270" />,
        label: 'Responsive communication'
      }, {
        icon: <LockIcon className="h-5 w-5" data-id="element-2271" />,
        label: 'Secure checkout'
      }].map((item, i) => <div key={i} className="flex flex-col items-center gap-1.5" data-id="element-2272">
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white" data-id="element-2273">
              {item.icon}
            </div>
            <span className="text-[10px] text-gray-300 text-center leading-tight" data-id="element-2274">
              {item.label}
            </span>
          </div>)}
      </div>
    </div>
  </section>;
// ═══ SECTION 10: CUSTOMERS LOVE US ═══
const CustomersLoveUs = () => <section id="customers-love-us" className="py-6 md:py-10 bg-white" data-id="element-2275">
    <div className="container mx-auto px-4 max-w-5xl" data-id="element-2276">
      <h2 className="text-center text-xl md:text-2xl font-bold text-[#1a1a2e] mb-6" style={{
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-2277">
        Customers love us
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-2278">
        {FULL_REVIEWS.map((r, i) => <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-2279">
            <div className="flex gap-0.5 mb-1.5" data-id="element-2280">
              {[...Array(5)].map((_, j) => <StarIcon key={j} className="h-3.5 w-3.5 text-amber-500 fill-amber-500" data-id="element-2281" />)}
            </div>
            <p className="text-gray-700 text-sm italic leading-relaxed mb-2" data-id="element-2282">
              "{r.quote}"
            </p>
            <div className="flex items-center gap-2" data-id="element-2283">
              <span className="text-[#1a1a2e] text-sm font-semibold" data-id="element-2284">
                — {r.name}
              </span>
              <span className="text-green-600 text-[10px] font-medium flex items-center gap-0.5" data-id="element-2285">
                <CheckIcon className="h-3 w-3" data-id="element-2286" /> Verified Customer
              </span>
            </div>
          </div>)}
      </div>
    </div>
  </section>;
// ═══ SECTION 11: FAQ ═══
const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return <section className="py-6 md:py-10 bg-gray-50" data-id="element-2287">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-2288">
        <h2 className="text-center text-xl md:text-2xl font-bold text-[#1a1a2e] mb-6" style={{
        fontFamily: "'Playfair Display', Georgia, serif"
      }} data-id="element-2289">
          Questions? We've Got Answers.
        </h2>
        <div className="space-y-1.5" data-id="element-2290">
          {FAQ_ITEMS.map((item, idx) => {
          const isOpen = openIdx === idx;
          return <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-2291">
                <button onClick={() => setOpenIdx(isOpen ? null : idx)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors" data-id="element-2292">
                  <span className="font-semibold text-[#1a1a2e] text-sm pr-4" data-id="element-2293">
                    {item.q}
                  </span>
                  <ChevronDownIcon className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-2294" />
                </button>
                {isOpen && <div className="px-4 pb-3 border-t border-gray-100" data-id="element-2295">
                    {item.a.split('\n\n').map((p, i) => <p key={i} className="text-gray-600 text-sm mb-2 last:mb-0 leading-relaxed mt-2" data-id="element-2296">
                        {p}
                      </p>)}
                  </div>}
              </div>;
        })}
        </div>
      </div>
    </section>;
};
// ═══ EXPORT ═══
export const LandingV5 = () => {
  return <div className="w-full min-h-screen bg-white text-gray-900" data-id="element-2297">
      <AnnouncementBar data-id="element-2298" />
      <main data-id="element-2299">
        <HeroProductSection data-id="element-2300" />
        <SocialProofStrip data-id="element-2301" />
        <SpecsAndAbout data-id="element-2302" />
        <GiftSection data-id="element-2303" />
        <LookInsideSection data-id="element-2304" />
        <ThreeColumnInfo data-id="element-2305" />
        <WhoAmIBuyingFrom data-id="element-2306" />
        <CustomersLoveUs data-id="element-2307" />
        <FAQSection data-id="element-2308" />
      </main>
      <Footer data-id="element-2309" />
    </div>;
};